import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { resolveImage } from '@/lib/image';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
    const { cartItems, getCartTotal, clearCart } = useCart();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const total = getCartTotal();

    const handleCheckout = async () => {
        if (cartItems.length === 0) {
            toast.error('Your cart is empty');
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.post(`${API}/checkout/create-session`, {
                cart_items: cartItems,
                origin_url: window.location.origin,
                email: email || null
            });

            if (response.data.url) {
                // Redirect to Stripe Checkout
                window.location.href = response.data.url;
            } else {
                throw new Error('No checkout URL received');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            toast.error('Failed to create checkout session', {
                description: 'Please try again or contact support.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (cartItems.length === 0) {
        return (
            <div data-testid="checkout-empty" className="min-h-screen bg-brand-bg pt-24 flex items-center justify-center">
                <div className="text-center">
                    <p className="font-space text-slate-400 text-lg mb-6">Your cart is empty</p>
                    <Button
                        onClick={() => navigate('/merch')}
                        className="bg-white text-black hover:bg-slate-200 rounded-none px-8 py-4 font-bold uppercase tracking-widest"
                    >
                        SHOP MERCH
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="checkout-page" className="min-h-screen bg-brand-bg pt-24">
            <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
                {/* Back Button */}
                <button 
                    onClick={() => navigate(-1)}
                    data-testid="back-button"
                    className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-space text-sm uppercase tracking-widest">Back</span>
                </button>

                <h1 className="font-syne font-extrabold text-4xl md:text-5xl text-white tracking-tighter uppercase mb-12">
                    CHECKOUT
                </h1>

                <div className="grid md:grid-cols-2 gap-12">
                    {/* Order Summary */}
                    <div data-testid="order-summary">
                        <h2 className="font-syne font-bold text-xl text-white mb-6">ORDER SUMMARY</h2>
                        <div className="space-y-4 mb-8">
                            {cartItems.map((item) => (
                                <div 
                                    key={`${item.product_id}-${item.size}`}
                                    className="flex justify-between items-center py-4 border-b border-brand-border"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 bg-brand-highlight overflow-hidden">
                                            <img 
                                                src={resolveImage(item.image_url)} 
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div>
                                            <p className="font-space text-white text-sm">{item.name}</p>
                                            {item.size && (
                                                <p className="font-mono text-xs text-slate-500">Size: {item.size}</p>
                                            )}
                                            <p className="font-mono text-xs text-slate-500">Qty: {item.quantity}</p>
                                        </div>
                                    </div>
                                    <p className="font-space text-white">
                                        ${(item.price * item.quantity).toFixed(2)}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-brand-border">
                            <span className="font-syne font-bold text-white uppercase">Total</span>
                            <span data-testid="checkout-total" className="font-syne font-bold text-2xl text-white">
                                ${total.toFixed(2)}
                            </span>
                        </div>
                    </div>

                    {/* Payment Section */}
                    <div data-testid="payment-section">
                        <h2 className="font-syne font-bold text-xl text-white mb-6">PAYMENT</h2>
                        
                        <div className="space-y-6">
                            {/* Email Input */}
                            <div>
                                <label className="font-mono text-xs text-slate-500 uppercase tracking-widest mb-2 block">
                                    Email (optional - for receipt)
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    data-testid="checkout-email-input"
                                    className="w-full bg-black/50 border-b border-white/20 focus:border-white outline-none px-0 py-4 text-white placeholder:text-neutral-600 rounded-none transition-colors font-space"
                                />
                            </div>

                            {/* Checkout Button */}
                            <Button
                                onClick={handleCheckout}
                                disabled={isLoading}
                                data-testid="pay-now-button"
                                className="w-full bg-white text-black hover:bg-slate-200 rounded-none py-6 font-bold uppercase tracking-widest flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        PROCESSING...
                                    </>
                                ) : (
                                    <>
                                        PAY ${total.toFixed(2)}
                                    </>
                                )}
                            </Button>

                            {/* Security Badge */}
                            <div className="flex items-center justify-center gap-2 text-slate-500">
                                <ShieldCheck className="w-4 h-4" />
                                <span className="font-mono text-xs uppercase tracking-widest">
                                    Secure checkout powered by Stripe
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
